<script lang="ts">
export default {
  name: 'AnalyticsHub',
};
</script>

<script setup lang="ts">
import { computed, inject, onMounted, ref, watch, provide, watchEffect } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { tableauService, volatilityModelService, stockDropModelService, cyberCubeService } from '@/services/api';
import { documentService } from '@/services/v2/DocumentService';
import { VolatilityModel } from '@/types/VolatilityModel';
import { TableauDashboard, TableauType } from '@/types/TableauDashboard';
import { Store } from 'vuex';
import { AppState } from '@/store';
import { Document, DocumentDomainTypeAnalytics, DocumentType } from '@/types/v2/Document';
import {
  HandshakeIcon,
  HouseIcon,
  MegaphoneIcon,
  ShieldQuestionIcon,
  TreePalmIcon,
  LaptopMinimalIcon,
  ChevronRightIcon,
  LayersIcon,
  TagIcon,
} from 'lucide-vue-next';
import CasualtyIcon from '@/components/icons/CasualtyIcon.vue';
import { StockDropModel } from '@/types/StockDropModel';
import { tenantName } from '@/utilities/AuthUtils.js';
import { Category, UploadType, getTypeLabel } from '@/utilities/Analytics.js';
import { CyberModelCard } from '@/types/analytics/Analytics.js';
import { AccordionContent, AccordionHeader, AccordionItem, AccordionRoot, AccordionTrigger } from 'reka-ui';
import TagService from '@/services/TagService';

// -----
// data
// -----

const tableauVizHeight = ref('');
const resourceTypeHeader = ref<HTMLElement | null>(null);
const loading = ref<boolean>(true);
const analyticsTypes = ref(new Map<number, string>());
const volatilityModels = ref<VolatilityModel[]>([]);
const stockDropModels = ref<StockDropModel[]>([]);
const cyberModels = ref<CyberModelCard[]>([]);
const dashboards = ref<TableauDashboard[]>([]);
const documents = ref<Document[]>([]);
const documentTypes = ref<DocumentType[]>([]);
const tableauTypes = ref<TableauType[]>([]);
const selectedCategoryType = ref<number>(-1);
const store = inject<Store<AppState>>('store');
const router = useRouter();
const route = useRoute();
const activeFilters = ref<string[]>([]);
const tags = ref<string[]>([]);
const activeTagFilters = ref<string[]>([]);

// ---------
// watchers
// ---------
watchEffect(() => {
  // Go back to the dashboard if the user navigates to the analytics hub
  if (route.path === '/analytics') {
    selectedCategoryType.value = -1;
  }
});

watch(
  () => route.params.categoryId,
  async (category) => {
    if (category) {
      selectedCategoryType.value = parseInt(category as string);
    }
  },
);

// -----------
// lifecycles
// -----------

onMounted(async () => {
  loading.value = true;
  await fetchMetadata();

  if (route.params.categoryId) {
    selectedCategoryType.value = parseInt(route.params.categoryId as string);
  }

  const category = route.meta.categoryType;
  if (category) {
    Array.from(analyticsTypes.value.entries()).forEach((analyticsType) => {
      if (analyticsType[1] === category) {
        selectedCategoryType.value = analyticsType[0];
      }
    });
  }

  await refresh();
});

// ----------
// functions
// ----------

async function refresh() {
  loading.value = true;
  computeTableauHeight();
  await Promise.all([
    fetchVolatilityModels(),
    fetchStockDropModels(),
    fetchTableauDashboards(),
    fetchCyberModels(),
    fetchDocuments(),
    fetchDocumentTypes(),
    fetchTableauTypes(),
    fetchTags(),
  ]);

  loading.value = false;
}

async function fetchMetadata() {
  const types = await tableauService.getAnalyticsTypes();
  analyticsTypes.value = types.reduce((acc, t) => {
    acc.set(t.id, t.name);
    return acc;
  }, new Map<number, string>());
}

async function fetchVolatilityModels() {
  volatilityModels.value = await volatilityModelService.list();
}

async function fetchStockDropModels() {
  stockDropModels.value = await stockDropModelService.list();
}

async function fetchCyberModels() {
  const details = await cyberCubeService.getAllCyberModelDetails();
  cyberModels.value = details.map((model) => {
    return {
      accountId: model.accountId,
      id: model.id,
      title: `${model.accountName} Cyber Model`,
      type: 'cyber',
      client: model.accountName,
      created_by: model.createdBy ? model.createdBy : undefined,
      created_on: model.createdDate,
      updated_on: model.updatedDate,
      published: model.published,
    };
  });
}

async function fetchTableauDashboards() {
  dashboards.value = await tableauService.getAllDashboards();
}

async function fetchDocuments() {
  // domain ID 3 = Analytics
  documents.value = await documentService.getDocuments({
    documentDomainId: 3,
  });
}

async function fetchDocumentTypes() {
  documentTypes.value = await documentService.getDocumentTypes();
}

async function fetchTableauTypes() {
  tableauTypes.value = await tableauService.getTableauTypes();
}

async function fetchTags() {
  tags.value = (await TagService.getTags()).map((tag) => tag.name);
}

function computeTableauHeight() {
  const calculatedHeight =
    window.innerHeight -
    (store?.state.resizerRegistry['header'].height ?? 0) -
    (resourceTypeHeader.value?.offsetHeight ?? 170);
  tableauVizHeight.value = `${calculatedHeight}px`;
}

function handleSelectResourceType(type: number) {
  selectedCategoryType.value = type;
  router.push({ path: `/analytics/category/${type}` });
  activeFilters.value = [];
  activeTagFilters.value = [];
}

function toggleFilter(filter: string) {
  if (activeFilters.value.includes(filter)) {
    activeFilters.value = activeFilters.value.filter((f) => f !== filter);
  } else {
    activeFilters.value.push(filter);
  }
}

function clearFilters() {
  activeFilters.value = [];
}

function toggleTagFilter(filter: string) {
  if (activeTagFilters.value.includes(filter)) {
    activeTagFilters.value = activeTagFilters.value.filter((f) => f !== filter);
  } else {
    activeTagFilters.value.push(filter);
  }
}

function clearTagFilters() {
  activeTagFilters.value = [];
}

function updated() {
  refresh();
}

// ---------
// computed
// ---------

const filteredDashboards = computed<TableauDashboard[]>(() => {
  // Advisory is a special case where we show dashboards not associated with an account
  if (selectedCategoryName.value === Category.Advisory) {
    if (activeFilters.value.length > 0) {
      return dashboards.value.filter(
        (dashboard) => dashboard.accountId === null && activeFilters.value.includes(dashboard.tableauType),
      );
    }
    return dashboards.value.filter((dashboard) => dashboard.accountId === null);
  }

  if (activeFilters.value.length > 0) {
    return dashboards.value.filter(
      (dashboard) =>
        dashboard.analyticsTypeId === selectedCategoryType.value &&
        dashboard.accountId !== null &&
        activeFilters.value.includes(dashboard.tableauType),
    );
  }
  return dashboards.value.filter(
    (dashboard) => dashboard.analyticsTypeId === selectedCategoryType.value && dashboard.accountId !== null,
  );
});

const filteredDocuments = computed<Document[]>(() => {
  let filteredDocs = documents.value;

  // Advisory is a special case where we show documents not associated with an account
  if (selectedCategoryName.value === Category.Advisory) {
    // Filter by type
    if (activeFilters.value.length > 0) {
      filteredDocs = filteredDocs.filter(
        (document) => document.accountId === null && activeFilters.value.includes(document.documentTypeName),
      );
    } else {
      filteredDocs = filteredDocs.filter((document) => document.accountId === null);
    }

    // Filter by tags
    if (activeTagFilters.value.length > 0) {
      filteredDocs = filteredDocs.filter((d) => {
        return d.tags && d.tags.some((tag) => activeTagFilters.value.includes(tag));
      });
    }

    return filteredDocs;
  }

  // Filter by type
  if (activeFilters.value.length > 0) {
    filteredDocs = filteredDocs.filter(
      (document) =>
        document.analyticsTypeId === selectedCategoryType.value &&
        document.accountId !== null &&
        activeFilters.value.includes(document.documentTypeName),
    );
  } else {
    filteredDocs = filteredDocs.filter(
      (document) => document.analyticsTypeId === selectedCategoryType.value && document.accountId !== null,
    );
  }

  // Filter by tags
  if (activeTagFilters.value.length > 0) {
    filteredDocs = filteredDocs.filter((d) => {
      return d.tags && d.tags.some((tag) => activeTagFilters.value.includes(tag));
    });
  }

  return filteredDocs;
});

const filteredDocumentTypes = computed<DocumentType[]>(() => {
  return documentTypes.value.filter((type) => type.analyticsTypeId === selectedCategoryType.value);
});

const documentTypesForFilters = computed<DocumentType[]>(() => {
  // Advisory is a special case where we show all document types
  if (selectedCategoryName.value === Category.Advisory) {
    const uniqueTypes = new Map();
    documentTypes.value.forEach((type) => {
      if (
        type.domainName === DocumentDomainTypeAnalytics &&
        type.name !== 'Miscellaneous' &&
        !uniqueTypes.has(type.name)
      ) {
        uniqueTypes.set(type.name, type);
      }
    });
    return [
      ...uniqueTypes.values(),
      {
        id: 0,
        name: 'Miscellaneous',
        domainId: 3,
        domainName: 'Analytics',
      },
    ];
  }

  return documentTypes.value.filter((type) => type.analyticsTypeId === selectedCategoryType.value);
});

const filteredTableauTypes = computed<TableauType[]>(() => {
  return tableauTypes.value.filter((type) => type.analyticsTypeId === selectedCategoryType.value);
});

const filteredVolatilityModels = computed<VolatilityModel[]>(() => {
  if (selectedCategoryName.value === Category.Other) {
    if (activeFilters.value.length > 0 && !activeFilters.value.includes('Volatility Model')) {
      return [];
    }
    return volatilityModels.value;
  }
  return [];
});

const selectedCategoryName = computed<string>(() => {
  return analyticsTypes.value.get(selectedCategoryType.value) ?? '';
});

const filteredStockDropModels = computed<StockDropModel[]>(() => {
  if (selectedCategoryName.value === Category.FLP) {
    if (activeFilters.value.length > 0 && !activeFilters.value.includes('Stock Drop Model')) {
      return [];
    }
    return stockDropModels.value;
  }
  return [];
});

const filteredCyberModels = computed<CyberModelCard[]>(() => {
  if (selectedCategoryName.value === Category.Cyber) {
    if (activeFilters.value.length > 0 && !activeFilters.value.includes('Cyber Model')) {
      return [];
    }
    return cyberModels.value;
  }
  return [];
});

const documentsForCategory = computed(() => {
  if (selectedCategoryName.value === Category.Advisory) {
    return documents.value.filter((document) => document.accountId === null);
  }

  return documents.value.filter(
    (document) => document.accountId !== null && document.analyticsTypeId === selectedCategoryType.value,
  );
});

const dashboardsForCategory = computed(() => {
  if (selectedCategoryName.value === Category.Advisory) {
    return dashboards.value.filter((dashboard) => dashboard.accountId === null);
  }

  return dashboards.value.filter(
    (dashboard) => dashboard.accountId !== null && dashboard.analyticsTypeId === selectedCategoryType.value,
  );
});

const visibleDocumentFilters = computed(() => {
  return documentTypesForFilters.value.filter((type) =>
    documentsForCategory.value.some((doc) => doc.documentTypeName === type.name),
  );
});

const visibleTableauFilters = computed(() => {
  return filteredTableauTypes.value.filter((type) =>
    dashboardsForCategory.value.some((dashboard) => dashboard.tableauType === type.name),
  );
});

const hasVolatilityModels = computed(() => {
  return selectedCategoryName.value === Category.Other && volatilityModels.value.length > 0;
});

const hasStockDropModels = computed(() => {
  return selectedCategoryName.value === Category.FLP && stockDropModels.value.length > 0;
});

const hasCyberModels = computed(() => {
  return selectedCategoryName.value === Category.Cyber && cyberModels.value.length > 0;
});

// --------
// provide
// --------
provide('selectedCategoryType', selectedCategoryType);
</script>

<template>
  <div v-if="selectedCategoryType < 0" class="tw:m-auto tw:flex tw:flex-col tw:items-center tw:gap-4 tw:pt-8">
    <div class="tw:flex tw:flex-col tw:items-center">
      <h2>Analytics Resource</h2>
      <div>Please choose from one of the categories below</div>
    </div>

    <div class="tw:flex tw:w-120 tw:flex-wrap tw:justify-center tw:gap-4 tw:pt-2">
      <template v-for="analyticsType in analyticsTypes.entries()" v-bind:key="analyticsType[0]">
        <div class="tw:group tw:tooltip tw:pointer-events-auto tw:z-20 tw:cursor-pointer">
          <div
            class="tw:flex tw:h-24 tw:w-36 tw:flex-col tw:justify-center tw:rounded-lg tw:border tw:border-solid tw:border-blue-200 tw:bg-white tw:shadow-md tw:transition-all tw:duration-150 tw:group-hover:border-blue-200 tw:group-hover:bg-blue-50 tw:group-hover:shadow-none"
            @click="handleSelectResourceType(analyticsType[0])">
            <div class="tw:text-black-500 tw:flex tw:flex-col tw:items-center tw:gap-2 tw:self-center tw:p-2">
              <HouseIcon v-if="analyticsType[1] === Category.Property" class="tw:text-gray-400" />
              <CasualtyIcon v-if="analyticsType[1] === Category.Casualty" class="tw:h-7 tw:w-7 tw:fill-gray-400" />
              <HandshakeIcon v-if="analyticsType[1] === Category.FLP" class="tw:text-gray-400" />
              <TreePalmIcon v-if="analyticsType[1] === Category.Captives" class="tw:text-gray-400" />
              <ShieldQuestionIcon v-if="analyticsType[1] === Category.Other" class="tw:text-gray-400" />
              <MegaphoneIcon v-if="analyticsType[1] === Category.Advisory" class="tw:text-gray-400" />
              <LaptopMinimalIcon v-if="analyticsType[1] === Category.Cyber" class="tw:text-gray-400" />

              <h2 class="tw:text-center tw:text-sm tw:font-medium">{{ analyticsType[1] }}</h2>
            </div>
          </div>
        </div>
      </template>
    </div>
    <a class="tw:btn tw:mt-6 tw:btn-sm tw:btn-primary" href="mailto:analytics@cacgroup.com">
      Contact {{ tenantName() }} Analytics
    </a>
  </div>

  <div v-if="selectedCategoryType > -1" class="tw:relative tw:flex tw:h-full">
    <aside class="tw:min-w-60 tw:max-w-60 tw:border-r tw:border-gray-300 tw:bg-gray-100 tw:p-4">
      <nav class="tw:sticky tw:top-20">
        <h2 class="tw:text-sm tw:font-semibold tw:uppercase tw:tracking-wide tw:text-gray-700">Categories</h2>

        <div class="tw:pt-1 tw:pb-8">
          <div
            v-for="(t, i) in analyticsTypes.entries()"
            :key="t[0]"
            :aria-label="t[1]"
            :tabindex="i"
            role="button"
            class="tw:rounded-md tw:border tw:border-transparent tw:px-3 tw:py-1.5 tw:text-sm tw:transition-all tw:select-none tw:hover:bg-gray-200"
            :class="{ 'tw:bg-specialty-teal/30 tw:hover:bg-specialty-teal/30 tw:font-medium': selectedCategoryType === t[0] }"
            @click="handleSelectResourceType(t[0])">
            {{ t[1] }}
          </div>
        </div>

        <!-- filters -->
        <AccordionRoot type="multiple" collapsible class="tw:flex tw:flex-col tw:gap-4">
          <AccordionItem value="typeFilters">
            <AccordionHeader>
              <AccordionTrigger
                class="tw:group tw:flex tw:w-full tw:items-center tw:justify-between tw:text-left">
                <div class="tw:flex tw:items-center tw:gap-2">
                  <ChevronRightIcon
                    class="tw:h-3.5 tw:w-3.5 tw:shrink-0 tw:text-gray-500 tw:transition-transform tw:duration-30 tw:group-data-[state=open]:rotate-90" />
                  <h2 class="tw:text-sm tw:font-semibold tw:uppercase tw:tracking-wide tw:text-gray-700">Types</h2>
                  <span v-if="activeFilters.length > 0" class="tw:badge tw:badge-soft tw:badge-sm">{{
                    activeFilters.length
                  }}</span>
                  <button
                    v-visible="activeFilters.length > 0"
                    class="tw:btn tw:rounded-lg tw:bg-agency-red/70 tw:text-white tw:btn-xs"
                    @click.stop="clearFilters">
                    Clear
                  </button>
                </div>
              </AccordionTrigger>
            </AccordionHeader>
            <AccordionContent class="tw:overflow-hidden">
              <div class="tw:mt-1 tw:flex tw:flex-col tw:gap-0.5">
                <!-- Document Filters -->
                <div
                  v-for="(type, i) in visibleDocumentFilters"
                  :key="type.id"
                  :aria-label="type.name"
                  :tabindex="i"
                  data-testid="doc-filter"
                  role="button"
                  class="tw:rounded tw:px-2 tw:py-1.5 tw:text-sm tw:transition-all tw:duration-75 tw:hover:bg-gray-200"
                  :class="{
                    'tw:font-medium tw:bg-specialty-teal/20 tw:hover:bg-specialty-teal/20': activeFilters.includes(
                      type.name,
                    ),
                  }"
                  @click="toggleFilter(type.name)">
                  <div class="tw:flex tw:items-start tw:gap-2">
                    <LayersIcon class="tw:h-4 tw:w-4 tw:shrink-0 tw:text-gray-500 tw:mt-0.5" />
                    <div class="tw:flex tw:flex-col tw:gap-0.5">
                      <span>{{ type.name }}</span>
                      <span class="tw:text-xs tw:italic tw:text-gray-600">{{
                        getTypeLabel(type.name, UploadType.Document)
                      }}</span>
                    </div>
                  </div>
                </div>

                <!-- Tableau Type Filters -->
                <div
                  v-for="(type, i) in visibleTableauFilters"
                  :key="type.id"
                  :aria-label="type.name"
                  :tabindex="i"
                  role="button"
                  class="tw:rounded tw:px-2 tw:py-1.5 tw:text-sm tw:transition-all tw:duration-75 tw:hover:bg-gray-200"
                  :class="{
                    'tw:font-medium tw:bg-specialty-teal/20 tw:hover:bg-specialty-teal/20': activeFilters.includes(
                      type.name,
                    ),
                  }"
                  @click="toggleFilter(type.name)">
                  <div class="tw:flex tw:items-start tw:gap-2">
                    <LayersIcon class="tw:h-4 tw:w-4 tw:shrink-0 tw:text-gray-500 tw:mt-0.5" />
                    <div class="tw:flex tw:flex-col tw:gap-0.5">
                      <span>{{ type.name }}</span>
                      <span class="tw:italic tw:text-xs tw:text-gray-600">{{
                        getTypeLabel(type.name, UploadType.TableauDashboard)
                      }}</span>
                    </div>
                  </div>
                </div>

                <!-- Volatility Model Filter (only shows for Other) -->
                <div
                  v-if="hasVolatilityModels"
                  role="button"
                  class="tw:rounded tw:px-2 tw:py-1.5 tw:text-sm tw:transition-all tw:duration-75 tw:hover:bg-gray-200"
                  :class="{
                    'tw:font-medium tw:bg-specialty-teal/30 tw:hover:bg-specialty-teal/30':
                      activeFilters.includes('Volatility Model'),
                  }"
                  @click="toggleFilter('Volatility Model')">
                  <div class="tw:flex tw:items-center tw:gap-2">
                    <LayersIcon class="tw:h-4 tw:w-4 tw:shrink-0 tw:text-gray-500" />
                    <span>Volatility Model</span>
                  </div>
                </div>

                <!-- Stock Drop Model Filter (only shows for FLP) -->
                <div
                  v-if="hasStockDropModels"
                  role="button"
                  class="tw:rounded tw:px-2 tw:py-1.5 tw:text-sm tw:transition-all tw:duration-75 tw:hover:bg-gray-200"
                  :class="{
                    'tw:font-medium tw:bg-specialty-teal/30 tw:hover:bg-specialty-teal/30':
                      activeFilters.includes('Stock Drop Model'),
                  }"
                  @click="toggleFilter('Stock Drop Model')">
                  <div class="tw:flex tw:items-center tw:gap-2">
                    <LayersIcon class="tw:h-4 tw:w-4 tw:shrink-0 tw:text-gray-500" />
                    <span>Stock Drop Model</span>
                  </div>
                </div>

                <!-- Cyber Model Filter (only shows for Cyber) -->
                <div
                  v-if="hasCyberModels"
                  role="button"
                  class="tw:rounded tw:px-2 tw:py-1.5 tw:text-sm tw:transition-all tw:duration-75 tw:hover:bg-gray-200"
                  :class="{
                    'tw:font-medium tw:bg-specialty-teal/30 tw:hover:bg-specialty-teal/30':
                      activeFilters.includes('Cyber Model'),
                  }"
                  @click="toggleFilter('Cyber Model')">
                  <div class="tw:flex tw:items-center tw:gap-2">
                    <LayersIcon class="tw:h-4 tw:w-4 tw:shrink-0 tw:text-gray-500" />
                    <span>Cyber Model</span>
                  </div>
                </div>
              </div>
            </AccordionContent>
          </AccordionItem>
          <AccordionItem value="tagFilters">
            <AccordionHeader>
              <AccordionTrigger class="tw:group tw:flex tw:w-full tw:items-center tw:justify-between tw:text-left">
                <div class="tw:flex tw:items-center tw:gap-2">
                  <ChevronRightIcon
                    class="tw:h-3.5 tw:w-3.5 tw:shrink-0 tw:text-gray-500 tw:transition-transform tw:duration-30 tw:group-data-[state=open]:rotate-90" />
                  <h2 class="tw:text-sm tw:font-semibold tw:tracking-wide tw:text-gray-700 tw:uppercase">Tags</h2>
                  <span v-if="activeTagFilters.length > 0" class="tw:badge tw:badge-soft tw:badge-sm">{{
                    activeTagFilters.length
                  }}</span>
                  <button
                    v-visible="activeTagFilters.length > 0"
                    class="tw:btn tw:rounded-lg tw:bg-agency-red/70 tw:text-white tw:btn-xs"
                    @click.stop="clearTagFilters">
                    Clear
                  </button>
                </div>
              </AccordionTrigger>
            </AccordionHeader>
            <AccordionContent class="tw:overflow-hidden">
              <div class="tw:flex tw:flex-col tw:gap-0.5">
                <!-- Tags -->
                <div
                  v-for="(tag, i) in tags"
                  :key="tag"
                  :aria-label="tag"
                  :tabindex="i"
                  role="button"
                  class="tw:rounded tw:px-2 tw:py-1.5 tw:text-sm tw:transition-all tw:duration-75 tw:hover:bg-gray-200"
                  :class="{
                    'tw:bg-specialty-teal/20 tw:font-medium tw:hover:bg-specialty-teal/20':
                      activeTagFilters.includes(tag),
                  }"
                  @click="toggleTagFilter(tag)">
                  <div class="tw:flex tw:items-start tw:gap-2">
                    <TagIcon class="tw:mt-0.5 tw:h-4 tw:w-4 tw:shrink-0 tw:text-gray-500" />
                    <div class="tw:flex tw:flex-col tw:gap-0.5">
                      {{ tag }}
                    </div>
                  </div>
                </div>
              </div>
            </AccordionContent>
          </AccordionItem>
        </AccordionRoot>
      </nav>
    </aside>

    <router-view
      :loading="loading"
      :dashboards="filteredDashboards"
      :volatility-models="filteredVolatilityModels"
      :stock-drop-models="filteredStockDropModels"
      :cyber-models="filteredCyberModels"
      :document-types="filteredDocumentTypes"
      :tableau-types="filteredTableauTypes"
      :documents="filteredDocuments"
      :selectedCategoryType="selectedCategoryType"
      :selectedCategoryName="selectedCategoryName"
      @updated="updated()" />
  </div>
</template>

